// NGXS actions must stay newable classes: withActions/createDispatchMap in
// @res/ngg-core is typed over ActionDef, which requires a `new (...args)`
// signature, and the dispatcher matches action results by reference identity.
// The parameterless ones therefore carry a mirror instance field so they are
// not classes of statics only. NGXS resolves the type from constructor.type
// first, so the dispatched string is unchanged.
import { DateRangeType } from '@res/ng-data-access';
import { OrOrOrderQuery, TypesaurusCoreDoc } from '@res/ngg-core';
import { AdvancedFilterModel, FilterModel, GridApi, IServerSideGetRowsRequest } from 'ag-grid-community';
import { RentModel, RentRef, RentUpdateData } from '../../schema/schema.interface';
import { RentStateModel } from './rent.store';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace RentActions {
  export class GetAllRent {
    static readonly type = '[Rent] GetAllRent';
    constructor(public stateSelectorName = 'rents') {}
  }
  export class OnGetAllQueryRent {
    static readonly type = '[Rent] OnGetAllQuerryRent';
    constructor(
      public request: OrOrOrderQuery<RentModel>[],
      public stateSelectorName: keyof Pick<RentStateModel, 'rents' | 'doneRents'> = 'rents',
    ) {}
  }

  export class OnQueryRent {
    // Distinct from OnGetAllQueryRent above. They shared one type string, and NGXS routes purely on
    // that string, so either dispatch invoked BOTH handlers: the Events grid's
    // IServerSideGetRowsRequest reached the realtime handler that expects a query-helper array, and
    // the realtime dispatch reached the paginated handler with the wrong shape.
    static readonly type = '[Rent] OnQueryRent';
    constructor(
      public request: IServerSideGetRowsRequest,
      public stateSelectorName: keyof Pick<RentStateModel, 'rents' | 'doneRents'> = 'rents',
      public isResetAfterFilterOrSort = false,
    ) {}
  }
  export class OnGetAllRent {
    static readonly type = '[Rent] OnGetAllRent';
    constructor(public stateSelectorName = 'rents') {}
  }

  export class InitRentsCount {
    static readonly type = '[Rent] InitRentCount';
    readonly type = InitRentsCount.type;
  }

  export class InitRents {
    static readonly type = '[Rent] InitRents';
    readonly type = InitRents.type;
  }
  export class CountRents {
    static readonly type = '[Rent] CountRents';
    readonly type = CountRents.type;
  }

  export class CloseRentsConnection {
    static readonly type = '[Rent] CloseRentsConnection';
    readonly type = CloseRentsConnection.type;
  }

  export class GetRentById {
    static readonly type = '[Rent] GetRentById';
    readonly type = GetRentById.type;
  }

  export class UpdateRent {
    static readonly type = '[Rent] UpdateRent';
    constructor(
      public rentData: Partial<RentUpdateData>,
      public ref: RentRef,
    ) {}
  }

  export class SetFilter {
    static readonly type = '[Rent] SetFilter';
    constructor(public filter: Partial<FilterModel | AdvancedFilterModel | null>) {}
  }

  export class UpdateRentResult {
    static readonly type = '[Rent] UpdateRentResult';
    constructor(
      public data: Array<TypesaurusCoreDoc<RentModel> | null | undefined>,
      public stateSelectoreName: keyof RentStateModel,
    ) {}
  }

  export class DeleteRents {
    static readonly type = '[Rent] DeleteRents';
    constructor(public refs: RentRef[]) {}
  }

  export class PaginateQueryRent {
    static readonly type = '[Rent] PaginateQueryRent';
    constructor(
      public request: IServerSideGetRowsRequest,
      public isResetAfterFilterOrSort = false,
      public userId?: string,
    ) {}
  }
  export class RentError {
    static readonly type = '[Rent] RentError';
    constructor(public error: Error) {}
  }

  export class SaveRent {
    static readonly type = '[Rent] SaveRent';
    constructor(
      public rentData: RentUpdateData,
      public ref?: RentRef,
      /**
       * True only for the first write to a preallocated `ref`. It decides whether the document is
       * created, which is what stamps `createdAt`, or updated. Without it every save took the same
       * branch and a preallocated ref never got a creation timestamp at all.
       */
      public isInitialWrite = false,
    ) {
      console.log('saverent-action file', { rentData, ref, isInitialWrite });
    }
  }

  export class AddGrid {
    static readonly type = '[Rent] Add Grid';
    constructor(
      public uGridName: string,
      public api: GridApi,
    ) {}
  }

  export class GetSumOf {
    static readonly type = '[Rent] GetSumOf';
    constructor(
      public field: string,
      public timePeriod: DateRangeType,
    ) {}
  }

  export class GetSumsCountOf {
    static readonly type = '[Rent] GetSumsCountOf';
    constructor(
      public fields: {
        sumId: string;
        countId: string;
        countValue: number | string | boolean | Date;
      }[],
      public timePeriod: DateRangeType,
      public filterModel?: FilterModel | AdvancedFilterModel | null,
      public userId?: string,
    ) {}
  }
}
